import React, {forwardRef, useMemo, useState} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {krajRadnogNaloga} from '../../../../api/radniNalozi'
import notifyToast from '../../../../components/Toast/Toast'
import {fetchTable} from '../../../../api/tableRS'
import LoadingComponent from '../../../../components/LoadingComponent/LoadingComponent'
import QrCodeScanner from '../../../../components/QrCodeScanner/QrCodeScanner'
import clsx from 'clsx'
import {KTIcon} from '../../../../../_websoft/helpers'
import {useNavigate} from 'react-router-dom'
import {ToastContainer} from 'react-toastify'
import LokacijeRsTraka from '../../../../components/LokacijeRSTraka/LokacijeRsTraka'

interface Props {
    nalogaId: string
}
export type Ref = any

type TrakaRow = {
    TrakaRaw: string // what user typed / pasted / scanned (full string)
    TrakaId: string // extracted ID (first segment)
    KrajnjaKolicina: number | ''
    Lokacija: string

    duzina?: any
    tezina?: any
    sirina?: any
    rolna?: any
    proizvodjac?: any

    scannedByDevice: boolean // ✅ true only when QrCodeScanner filled it
    isScanned: boolean // ✅ computed
}

const normalizeScan = (value: string) =>
    String(value ?? '')
        .replace(/[\r\n\t]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

/**
 * ✅ Content-based QR validation:
 * - minimum 3 semicolons
 * - ends with "Alternativa" or "Alternativa doo" (case-insensitive)
 * - ignores trailing ';' and spaces
 *
 * Examples:
 * "43189; 51694; Nepoznato; Stteel; Alternativa"        => ✅ (4 semicolons)
 * "14; SZXC...; 0.50; 1250; Alternativa"                => ✅ (4 semicolons)
 * "14; 41201; teteftad; Alternativa"                    => ✅ (3 semicolons)
 */
const looksLikeAlternativaQr = (raw: string = ''): boolean => {
    const s = normalizeScan(raw)
    if (!s) return false

    const semicolons = (s.match(/;/g) || []).length
    if (semicolons < 3) return false

    // remove trailing semicolons and spaces
    const normalized = s.replace(/;+\s*$/, '').trim()

    return /(alternativa|alternativa doo)$/i.test(normalized)
}

// ✅ extract first segment as ID (works even if raw has extra parts)
const extractTrakaId = (raw: string = ''): string => {
    return normalizeScan(raw).split(';')[0].trim()
}

const emptyRow = (): TrakaRow => ({
    TrakaRaw: '',
    TrakaId: '',
    KrajnjaKolicina: '',
    Lokacija: '',
    duzina: undefined,
    tezina: undefined,
    sirina: undefined,
    rolna: undefined,
    proizvodjac: undefined,
    scannedByDevice: false,
    isScanned: false,
})

const KrajTraka = forwardRef<Ref, Props>(({nalogaId}, ref) => {
    const nalog = useMemo(() => nalogaId, [nalogaId])
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)

    const initialValues = {
        trake: [] as TrakaRow[],
    }

    const validationSchema = Yup.object({
        // Add validations later if needed
    })

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async () => {
            setLoading(true)

            const payloadData = {
                IdNaloga: nalog,
                BrojRolne: '',
                KrajnjaKolicina: '',
                Lokacija: '',
                trake: (formik.values.trake ?? []).map((t) => ({
                    TrakaRaw: t.TrakaRaw,
                    TrakaId: t.TrakaId,
                    KrajnjaKolicina: t.KrajnjaKolicina,
                    Lokacija: t.Lokacija,
                    isScanned: t.isScanned ? 1 : 0, // ✅ if backend wants number
                    // scannedByDevice: t.scannedByDevice ? 1 : 0, // optional
                })),
            }

            krajRadnogNaloga(payloadData)
                .then((response: any) => {
                    notifyToast(true, response.message)
                    setTimeout(() => navigate(-1), 1500)
                })
                .catch((error: any) => {
                    console.log(error)
                    notifyToast(false, error.message)
                })
                .finally(() => setLoading(false))
        },
    })

    const getTrakeData = (trakeId: string | number, rowId: number) => {
        const numericId = Number(trakeId)
        if (!numericId || Number.isNaN(numericId)) return

        return fetchTable(numericId)
            .then((response: any) => {
                const trake = [...(formik.values.trake ?? [])]
                const row = {...trake[rowId]}

                row.duzina = response.TrenutnaDuzina
                row.tezina = response?.Tezina
                row.sirina = response?.Sirina
                row.rolna = response?.BrojRolne
                row.proizvodjac = response?.repromaterijal?.proizvodjac?.PartnerNaziv

                trake[rowId] = row
                formik.setFieldValue('trake', trake)
            })
            .catch((error: any) => notifyToast(false, error.message))
    }

    const addRow = () => {
        const trake = [...(formik.values.trake ?? [])]
        trake.push(emptyRow())
        formik.setFieldValue('trake', trake)
    }

    const removeRow = (i: number) => {
        const trake = [...(formik.values.trake ?? [])]
        trake.splice(i, 1)
        formik.setFieldValue('trake', trake)
    }

    /**
     * ✅ Single source of truth for updating the input:
     * - rawValue: full value in input
     * - source: 'manual' or 'device'
     *
     * isScanned = looksLikeAlternativaQr(rawValue) OR source==='device'
     */
    const updateTrakaRaw = (index: number, rawValue: string, source: 'manual' | 'device') => {
        const trake = [...(formik.values.trake ?? [])]

        const cleaned = normalizeScan(rawValue)
        const trakaId = extractTrakaId(cleaned)

        const scannedByDevice = source === 'device'
        const isScanned = looksLikeAlternativaQr(cleaned) || scannedByDevice

        trake[index] = {
            ...trake[index],
            TrakaRaw: cleaned,
            TrakaId: trakaId,
            scannedByDevice,
            isScanned,
        }

        formik.setFieldValue('trake', trake)
    }

    return (
        <div className='w-100'>
            <ToastContainer />

            {/* Header */}
            <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-6'>
                <div>
                    <h2 className='fw-bolder text-dark mb-1'>Trake Info</h2>
                    <div className='text-muted'>
                        Radni nalog: <span className='fw-semibold text-dark'>{nalogaId}</span>
                    </div>
                </div>

                <button type='button' className='btn btn-light-primary' onClick={addRow}>
                    <KTIcon iconName='plus' className='fs-2 me-2' />
                    Dodaj traku
                </button>
            </div>

            {loading ? (
                <div className='card shadow-sm border-0'>
                    <div className='card-body p-6'>
                        <LoadingComponent />
                    </div>
                </div>
            ) : (
                <form onSubmit={formik.handleSubmit} noValidate>
                    <div className='d-flex flex-column gap-4'>
                        {/* Empty state */}
                        {!formik.values.trake?.length ? (
                            <div className='card shadow-sm border-0'>
                                <div className='card-body p-6 text-center'>
                                    <div className='text-muted mb-3'>Nema dodanih traka.</div>
                                    <button type='button' className='btn btn-primary' onClick={addRow}>
                                        <KTIcon iconName='plus' className='fs-2 me-2' />
                                        Dodaj prvu traku
                                    </button>
                                </div>
                            </div>
                        ) : null}

                        {/* Rows */}
                        {formik.values.trake?.map((row, i) => {
                            const hasInfo =
                                row?.duzina !== undefined ||
                                row?.tezina !== undefined ||
                                row?.sirina !== undefined ||
                                row?.rolna !== undefined ||
                                row?.proizvodjac

                            return (
                                <div key={i} className='card shadow-sm border-0'>
                                    <div className='card-body p-4 p-md-6'>
                                        {/* Row header */}
                                        <div className='d-flex align-items-start justify-content-between gap-3 mb-4'>
                                            <div>
                                                <div className='fw-bolder text-gray-900'>Traka #{i + 1}</div>

                                                <div className='mt-2 d-flex gap-2 flex-wrap'>
                                                    {row.isScanned ? (
                                                        <span className='badge badge-light-success fw-semibold'>Skenirano</span>
                                                    ) : (
                                                        <span className='badge badge-light-warning fw-semibold'>Nije skenirano</span>
                                                    )}

                                                    {row.scannedByDevice ? (
                                                        <span className='badge badge-light-info fw-semibold'>Kamera</span>
                                                    ) : (
                                                        <span className='badge badge-light fw-semibold text-muted'>Ručni unos</span>
                                                    )}
                                                </div>
                                            </div>

                                            <button
                                                type='button'
                                                className='btn btn-light-danger btn-sm'
                                                onClick={() => removeRow(i)}
                                                title='Ukloni traku'
                                            >
                                                <KTIcon iconName='minus' className='fs-2' />
                                            </button>
                                        </div>

                                        {/* Inputs row */}
                                        <div className='row g-4 align-items-end'>
                                            {/* Traka input + scan */}
                                            <div className='col-12 col-lg-6'>
                                                <label className='form-label fw-bold'>Broj trake</label>

                                                <div className='d-flex gap-3 align-items-stretch'>
                                                    <div
                                                        className='d-flex align-items-center justify-content-center rounded border bg-light'
                                                        style={{width: 54, minWidth: 54}}
                                                    >
                                                        <QrCodeScanner
                                                            onScan={(data: string) => {
                                                                const raw = String(data ?? '')
                                                                updateTrakaRaw(i, raw, 'device')

                                                                const id = extractTrakaId(raw)
                                                                if (id) getTrakeData(id, i)
                                                            }}
                                                        />
                                                    </div>

                                                    <input
                                                        type='text'
                                                        placeholder='Skeniraj ili upiši broj trake'
                                                        className={clsx('form-control form-control-lg form-control-solid', {
                                                            'is-invalid': !!row.TrakaRaw && !row.isScanned && row.TrakaRaw.includes(';'),
                                                        })}
                                                        value={row.TrakaRaw ?? ''}
                                                        onChange={(e) => {
                                                            // manual typing overrides "device" source
                                                            updateTrakaRaw(i, e.target.value, 'manual')
                                                        }}
                                                        onBlur={() => {
                                                            const id = extractTrakaId(String(formik.values.trake?.[i]?.TrakaRaw ?? ''))
                                                            if (id) getTrakeData(id, i)
                                                        }}
                                                    />
                                                </div>

                                                {/* Helpful hint only if user typed something with ; but it’s not valid */}
                                                {!!row.TrakaRaw && row.TrakaRaw.includes(';') && !row.isScanned ? (
                                                    <div className='form-text text-danger'>
                                                        Neispravan QR kod format.
                                                    </div>
                                                ) : null}
                                            </div>

                                            {/* Krajnja Kolicina */}
                                            <div className='col-12 col-lg-3'>
                                                <label className='form-label fw-bold'>Krajnja težina</label>
                                                <input
                                                    type='number'
                                                    placeholder='Težina'
                                                    className={clsx('form-control form-control-lg form-control-solid')}
                                                    value={row.KrajnjaKolicina ?? ''}
                                                    onChange={(e) => {
                                                        const raw = e.target.value
                                                        const newValue = raw === '' ? '' : Number(raw)
                                                        const maxTezina = Number(row.tezina ?? 0)

                                                        if (newValue === '') {
                                                            formik.setFieldValue(`trake[${i}].KrajnjaKolicina`, '')
                                                            return
                                                        }

                                                        if (!maxTezina || newValue <= maxTezina) {
                                                            formik.setFieldValue(`trake[${i}].KrajnjaKolicina`, newValue)
                                                        } else {
                                                            formik.setFieldValue(`trake[${i}].KrajnjaKolicina`, maxTezina)
                                                        }
                                                    }}
                                                />
                                            </div>

                                            {/* Lokacija */}
                                            <div className='col-12 col-lg-3 fv-row'>
                                                <label className='form-label fw-bold'>Lokacija trake</label>

                                                <LokacijeRsTraka
                                                    value={formik.values.trake?.[i]?.Lokacija}
                                                    onChange={(name: any, value: any) => {
                                                        formik.setFieldValue(`trake[${i}].Lokacija`, value)
                                                    }}
                                                    errors={(formik.errors as any)?.trake?.[i]?.Lokacija}
                                                    touched={(formik.touched as any)?.trake?.[i]?.Lokacija}
                                                />

                                                {(formik.touched as any)?.trake?.[i]?.Lokacija &&
                                                    (formik.errors as any)?.trake?.[i]?.Lokacija && (
                                                        <div className='fv-plugins-message-container'>
                                                            <div className='fv-help-block'>{(formik.errors as any)?.trake?.[i]?.Lokacija}</div>
                                                        </div>
                                                    )}
                                            </div>
                                        </div>

                                        {/* Info panel */}
                                        {hasInfo ? (
                                            <div className='mt-4 p-3 rounded border bg-light'>
                                                <div className='row g-2 small'>
                                                    <div className='col-6 col-md-4'>
                                                        <span className='text-muted'>Dužina</span>
                                                        <div className='fw-semibold text-danger'>{row.duzina ?? '-'}</div>
                                                    </div>
                                                    <div className='col-6 col-md-4'>
                                                        <span className='text-muted'>Težina</span>
                                                        <div className='fw-semibold text-danger'>{row.tezina ?? '-'}</div>
                                                    </div>
                                                    <div className='col-6 col-md-4'>
                                                        <span className='text-muted'>Širina</span>
                                                        <div className='fw-semibold text-danger'>{row.sirina ?? '-'}</div>
                                                    </div>
                                                    <div className='col-6 col-md-4'>
                                                        <span className='text-muted'>Broj rolne</span>
                                                        <div className='fw-semibold text-danger'>{row.rolna ?? '-'}</div>
                                                    </div>
                                                    <div className='col-12 col-md-8'>
                                                        <span className='text-muted'>Proizvođač</span>
                                                        <div className='fw-semibold text-danger'>{row.proizvodjac ?? '-'}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            )
                        })}

                        {/* Footer */}
                        <div className='card shadow-sm border-0'>
                            <div className='card-body d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3'>
                                <div className='text-muted'>
                                    Provjeri unesene trake pa klikni <span className='fw-semibold'>Potvrdi</span>.
                                </div>

                                <div className='ms-auto'>
                                    <button
                                        type='submit'
                                        className='btn btn-primary btn-lg px-4'
                                        disabled={loading || formik.isSubmitting || !formik.isValid}
                                    >
                                        {!loading ? (
                                            <>
                                                <i className='bi bi-check2-circle me-2' />
                                                Potvrdi
                                            </>
                                        ) : (
                                            <span className='d-inline-flex align-items-center gap-2'>
                        Please wait...
                        <span className='spinner-border spinner-border-sm align-middle' />
                      </span>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            )}
        </div>
    )
})

export default KrajTraka
